import { FormEvent, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  createInvitation,
  deleteGodown,
  getDistrict,
  removeGodownAgent,
  updateGodown,
} from "../lib/api";
import type { GodownStatus, Invitation, PortalUser } from "../lib/api";
import {
  GODOWN_STATUS_META,
  GODOWN_STATUS_ORDER,
  StatusTag,
} from "../lib/godownStatus";
import {
  DASH,
  fmtInt,
  fullName,
  isPass,
  shortDate,
  useSHGodowns,
  useSHInvitations,
  useSHReports,
  useSHUsers,
} from "../statehead/data";
import { grainLabel } from "../statehead/Reports";
import { GrainParams } from "./GrainParams";

/** QC agents (godown_qc_agent) holding a godown scope for this godown. */
function godownAgents(users: PortalUser[], godownId: number): PortalUser[] {
  return users.filter(
    (u) =>
      u.role === "godown_qc_agent" &&
      (u.scopes ?? []).some(
        (s) => s.scope_type === "godown" && s.scope_id === godownId,
      ),
  );
}

type BasePath = "/sh" | "/dh";

/** The godown management page, shared by both consoles. `basePath`
 * ("/sh" or "/dh") drives the breadcrumb, back target and report
 * links so the same screen serves a state head (reached from a
 * district) and a district head (reached from their godowns list). */
export function GodownDetail({ basePath }: { basePath: BasePath }) {
  const { id } = useParams();
  const godownId = Number(id);
  const navigate = useNavigate();

  const godownsQ = useSHGodowns();
  const reportsQ = useSHReports();
  const invitesQ = useSHInvitations();
  const usersQ = useSHUsers();

  const [editing, setEditing] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const say = (m: string) => {
    setToast(m);
    window.setTimeout(() => setToast(null), 3200);
  };

  const godown = godownsQ.data?.godowns.find((g) => g.id === godownId) ?? null;
  const districtId = godown?.district_id ?? null;
  const districtQ = useQuery({
    queryKey: ["district", districtId],
    queryFn: () => getDistrict(districtId as number),
    enabled: districtId != null,
  });
  const district = districtQ.data?.district ?? null;

  const invites = invitesQ.data?.invitations ?? [];
  const users = usersQ.data?.users ?? [];

  const reports = useMemo(
    () =>
      [...(reportsQ.data?.reports ?? [])]
        .filter((r) => r.godown_id === godownId)
        .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")),
    [reportsQ.data, godownId],
  );

  const agents = godownAgents(users, godownId);
  const pendingInvites = invites.filter(
    (i) =>
      i.role === "godown_qc_agent" &&
      i.target_scope_type === "godown" &&
      i.target_scope_id === godownId &&
      !i.accepted_at &&
      !i.revoked_at,
  );
  const grain = grainLabel(godown);

  const passed = reports.filter((r) => isPass(r.verdict)).length;
  const passRate = reports.length
    ? Math.round((passed / reports.length) * 100)
    : null;

  const backTo =
    basePath === "/dh" ? "/dh/godowns" : `/sh/district/${districtId ?? ""}`;

  const crumb =
    basePath === "/dh" ? (
      <p className="crumb">
        <Link className="crumb-link" to="/dh/godowns">
          {(district?.name ?? "DISTRICT").toUpperCase()} / GODOWNS
        </Link>{" "}
        / {(godown?.name ?? "GODOWN").toUpperCase()}
      </p>
    ) : (
      <p className="crumb">
        <Link className="crumb-link" to="/sh/districts">
          DISTRICTS
        </Link>{" "}
        /{" "}
        <Link className="crumb-link" to={`/sh/district/${districtId ?? ""}`}>
          {(district?.name ?? "DISTRICT").toUpperCase()}
        </Link>{" "}
        / {(godown?.name ?? "GODOWN").toUpperCase()}
      </p>
    );

  if (godownsQ.isLoading) {
    return (
      <>
        {crumb}
        <div className="card">
          <div className="card-body">
            <div className="tray-cap">Loading…</div>
          </div>
        </div>
      </>
    );
  }
  if (!godown) {
    return (
      <>
        {crumb}
        <div className="card">
          <div className="card-body">
            <div className="empty" style={{ border: 0, padding: "34px 20px" }}>
              <b>That godown isn't available</b>
              <p>It may have been removed, or you may not have access to it.</p>
              <Link className="btn" to={backTo}>
                Back
              </Link>
            </div>
          </div>
        </div>
      </>
    );
  }

  const agentLabel =
    (agents[0] && fullName(agents[0])) || pendingInvites[0]?.email || null;
  const recent = reports.slice(0, 10);

  return (
    <>
      {crumb}
      <div className="page-head">
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <h1>{godown.name}</h1>
            <StatusTag status={godown.status ?? "setup"} />
          </div>
          <p>
            {(godown.place ?? "").trim() || "Place not set"}
            {grain ? ` · ${grain.toLowerCase()}` : ""}
            {district ? ` · ${district.name} district` : ""} · QC agent{" "}
            {agentLabel ?? DASH}
          </p>
        </div>
        <div className="page-actions">
          <button className="btn" onClick={() => setEditing(true)}>
            Edit godown
          </button>
          <Link className="btn btn-gold" to={`${basePath}/reports?godown=${godown.id}`}>
            View reports
          </Link>
        </div>
      </div>

      <div className="stats">
        <div className="stat">
          <span>REPORTS</span>
          <b>{fmtInt(reports.length)}</b>
          <i>filed by field devices</i>
        </div>
        <div className="stat">
          <span>REPORTS PASSED</span>
          {passRate != null ? (
            <>
              <b>
                {passRate}
                <small style={{ fontSize: 14 }}>%</small>
              </b>
              <i className="up">{fmtInt(passed)} passed</i>
            </>
          ) : (
            <>
              <b>{DASH}</b>
              <i>no reports yet</i>
            </>
          )}
        </div>
        <div className="stat">
          <span>QC AGENTS</span>
          <b>{fmtInt(agents.length)}</b>
          <i>
            {pendingInvites.length
              ? `${pendingInvites.length} invite${pendingInvites.length === 1 ? "" : "s"} pending`
              : "on this godown"}
          </i>
        </div>
        <div className="stat">
          <span>GRAINS</span>
          <b>{fmtInt((godown.grain_configs ?? []).length)}</b>
          <i>configured here</i>
        </div>
      </div>

      <GrainParams godown={godown} />

      <QcAgentsCard
        godownId={godown.id}
        agents={agents}
        pending={pendingInvites}
        reportCountFor={(uid) => reports.filter((r) => r.user_id === uid).length}
        onInvited={(email) => say(`Invite sent to ${email}.`)}
      />

      <div className="card" style={{ padding: 0 }}>
        <div className="card-head">
          <div>
            <h2>Recent reports</h2>
            <p>The latest grading reports filed at this godown.</p>
          </div>
          <div className="right">
            <Link className="btn" to={`${basePath}/reports?godown=${godown.id}`}>
              View all
            </Link>
          </div>
        </div>
        {recent.length === 0 ? (
          <div style={{ padding: 22 }}>
            <div className="empty" style={{ border: 0 }}>
              <b>No reports yet</b>
              <p>Grading reports show here as field devices file them.</p>
            </div>
          </div>
        ) : (
          <div className="tscroll">
            <table className="stock" style={{ border: 0, borderRadius: 0 }}>
              <thead>
                <tr>
                  <th>REPORT ID</th>
                  <th>GRADED ON</th>
                  <th>GRAIN</th>
                  <th>VERDICT</th>
                  <th className="r">MOISTURE</th>
                  <th>QC AGENT</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((r) => {
                  const ok = isPass(r.verdict);
                  return (
                    <tr key={r.id}>
                      <td>
                        <b className="mono" style={{ fontSize: 12.5 }}>
                          {r.report_id}
                        </b>
                      </td>
                      <td>{shortDate(r.created_at)}</td>
                      <td>
                        <span className="mono">
                          {(r.grain_type || "maize").toUpperCase()}
                        </span>
                      </td>
                      <td>
                        <span className={"tag " + (ok ? "tag-live" : "tag-setup")}>
                          <span className="dot" />
                          {ok ? "Pass" : "Fail"}
                        </span>
                      </td>
                      <td className="r mono">
                        {r.moisture != null ? `${r.moisture}%` : DASH}
                      </td>
                      <td>{r.user_name || DASH}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <EditGodownDrawer
        open={editing}
        godownId={godown.id}
        initialName={godown.name}
        initialPlace={(godown.place ?? "").trim()}
        initialStatus={godown.status ?? "setup"}
        agentCount={agents.length}
        onClose={() => setEditing(false)}
        onSaved={() => {
          setEditing(false);
          say("Godown updated.");
        }}
        onDeleted={() => navigate(backTo)}
      />

      <div className={"toast" + (toast ? " show" : "")}>
        <span className="dot" />
        <span>{toast}</span>
      </div>
    </>
  );
}

// ------------------------------------------------------------------
// QC agents card — confirmed agents, pending invites, invite form
// ------------------------------------------------------------------

function QcAgentsCard({
  godownId,
  agents,
  pending,
  reportCountFor,
  onInvited,
}: {
  godownId: number;
  agents: PortalUser[];
  pending: Invitation[];
  reportCountFor: (userId: number) => number;
  onInvited: (email: string) => void;
}) {
  const qc = useQueryClient();
  const [inviting, setInviting] = useState(false);
  const [channel, setChannel] = useState<"email" | "phone">("email");
  const [contact, setContact] = useState("");
  const [confirmId, setConfirmId] = useState<number | null>(null);

  const mut = useMutation({
    mutationFn: () =>
      createInvitation({
        ...(channel === "email"
          ? { email: contact.trim() }
          : { phone: contact.trim() }),
        role: "godown_qc_agent",
        target_scope_type: "godown",
        target_scope_id: godownId,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sh-invitations"] });
      onInvited(contact.trim());
      setContact("");
      setInviting(false);
    },
  });

  const removeMut = useMutation({
    mutationFn: (userId: number) => removeGodownAgent(godownId, userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sh-users"] });
      setConfirmId(null);
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!contact.trim() || mut.isPending) return;
    mut.mutate();
  };

  return (
    <div className="card" style={{ padding: 0 }}>
      <div className="card-head">
        <div>
          <h2>QC agents</h2>
          <p>Field staff who grade samples at this godown.</p>
        </div>
        <div className="right">
          {inviting ? (
            <form
              onSubmit={submit}
              style={{ display: "flex", gap: 6, alignItems: "center" }}
            >
              <div
                style={{
                  display: "inline-flex",
                  border: "1px solid var(--line)",
                  borderRadius: 8,
                  overflow: "hidden",
                }}
              >
                {(["email", "phone"] as const).map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => {
                      setChannel(c);
                      setContact("");
                    }}
                    style={{
                      height: 34,
                      padding: "0 10px",
                      fontSize: 12,
                      border: 0,
                      cursor: "pointer",
                      background: channel === c ? "var(--red)" : "#fff",
                      color: channel === c ? "#fff" : "var(--muted)",
                    }}
                  >
                    {c === "email" ? "Email" : "Phone"}
                  </button>
                ))}
              </div>
              <input
                className="search"
                type={channel === "email" ? "email" : "tel"}
                value={contact}
                onChange={(e) => setContact(e.target.value)}
                placeholder={
                  channel === "email"
                    ? "agent@example.com"
                    : "+91 98765 43210"
                }
                autoFocus
                style={{ width: 200, height: 34 }}
              />
              <button type="submit" className="btn btn-gold" disabled={mut.isPending}>
                {mut.isPending ? "Sending…" : "Send invite"}
              </button>
              <button
                type="button"
                className="btn"
                onClick={() => {
                  setInviting(false);
                  setContact("");
                }}
              >
                Cancel
              </button>
            </form>
          ) : (
            <button className="btn" onClick={() => setInviting(true)}>
              Invite agent
            </button>
          )}
        </div>
      </div>

      {(mut.isError || removeMut.isError) && (
        <div style={{ padding: "0 20px" }}>
          <span className="tray-cap" style={{ color: "#B00020" }}>
            {((mut.error || removeMut.error) as Error).message}
          </span>
        </div>
      )}

      {agents.length === 0 && pending.length === 0 ? (
        <div style={{ padding: 22 }}>
          <div className="empty" style={{ border: 0 }}>
            <b>No agents yet</b>
            <p>Invite a QC agent to start grading samples at this godown.</p>
          </div>
        </div>
      ) : (
        <div className="tscroll">
          <table className="stock" style={{ border: 0, borderRadius: 0 }}>
            <thead>
              <tr>
                <th>AGENT</th>
                <th>WORK EMAIL</th>
                <th className="r">REPORTS FILED</th>
                <th>STATUS</th>
                <th className="r" />
              </tr>
            </thead>
            <tbody>
              {agents.map((a) => (
                <tr key={`u-${a.id}`}>
                  <td>
                    <b>{fullName(a) ?? a.email}</b>
                  </td>
                  <td>{a.email}</td>
                  <td className="r mono">{fmtInt(reportCountFor(a.id))}</td>
                  <td>
                    <span className="tag tag-live">
                      <span className="dot" />
                      Confirmed
                    </span>
                  </td>
                  <td className="r nowrap">
                    {confirmId === a.id ? (
                      <span
                        style={{
                          display: "inline-flex",
                          gap: 6,
                          alignItems: "center",
                        }}
                      >
                        <button
                          className="link"
                          style={{ color: "#B00020" }}
                          disabled={removeMut.isPending}
                          onClick={() => removeMut.mutate(a.id)}
                        >
                          {removeMut.isPending ? "Removing…" : "Confirm"}
                        </button>
                        <button
                          className="link soft"
                          onClick={() => setConfirmId(null)}
                        >
                          Cancel
                        </button>
                      </span>
                    ) : (
                      <button
                        className="link"
                        onClick={() => setConfirmId(a.id)}
                      >
                        Remove
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {pending.map((inv) => (
                <tr key={`i-${inv.id}`}>
                  <td>
                    <b style={{ color: "var(--muted)" }}>Invited</b>
                  </td>
                  <td>{inv.email ?? inv.phone}</td>
                  <td className="r mono">{DASH}</td>
                  <td>
                    <span className="tag tag-setup">
                      <span className="dot" />
                      Awaiting confirmation
                    </span>
                  </td>
                  <td />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------------
// Edit godown drawer (with a delete danger zone)
// ------------------------------------------------------------------

function EditGodownDrawer({
  open,
  godownId,
  initialName,
  initialPlace,
  initialStatus,
  agentCount,
  onClose,
  onSaved,
  onDeleted,
}: {
  open: boolean;
  godownId: number;
  initialName: string;
  initialPlace: string;
  initialStatus: GodownStatus;
  agentCount: number;
  onClose: () => void;
  onSaved: () => void;
  onDeleted: () => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState(initialName);
  const [place, setPlace] = useState(initialPlace);
  const [status, setStatus] = useState<GodownStatus>(initialStatus);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const seed = `${godownId}:${initialName}:${initialPlace}:${initialStatus}`;
  const [seenSeed, setSeenSeed] = useState(seed);
  if (seed !== seenSeed) {
    setSeenSeed(seed);
    setName(initialName);
    setPlace(initialPlace);
    setStatus(initialStatus);
    setConfirmDelete(false);
  }

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["sh-godowns"] });
  };

  const saveMut = useMutation({
    mutationFn: () =>
      updateGodown(godownId, {
        name: name.trim(),
        place: place.trim(),
        status,
      }),
    onSuccess: () => {
      invalidate();
      onSaved();
    },
  });

  const delMut = useMutation({
    mutationFn: () => deleteGodown(godownId),
    onSuccess: () => {
      invalidate();
      onDeleted();
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim() || saveMut.isPending) return;
    saveMut.mutate();
  };

  return (
    <>
      <div
        className={"scrim" + (open ? " open" : "")}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className={"drawer" + (open ? " open" : "")}
        role="dialog"
        aria-modal="true"
      >
        <div className="drawer-head">
          <div>
            <h2>Edit godown</h2>
            <p>Update the godown's name or place. Its reports stay attached.</p>
          </div>
          <button className="x" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <form onSubmit={submit} style={{ display: "contents" }}>
          <div className="drawer-body">
            <div className="field">
              <label>Godown name</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Sonbarsa Godown"
                autoFocus
              />
            </div>
            <div className="field">
              <label>
                Place<span className="hint">optional</span>
              </label>
              <input
                value={place}
                onChange={(e) => setPlace(e.target.value)}
                placeholder="Sonbarsa"
              />
            </div>
            <div className="field">
              <label>Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as GodownStatus)}
              >
                {GODOWN_STATUS_ORDER.map((s) => (
                  <option key={s} value={s}>
                    {GODOWN_STATUS_META[s].label}
                  </option>
                ))}
              </select>
            </div>
            {saveMut.isError && (
              <div
                className="notice"
                style={{ background: "#FDE8E8", borderColor: "#F5B5B5" }}
              >
                <p style={{ color: "#B00020" }}>
                  {(saveMut.error as Error).message}
                </p>
              </div>
            )}

            <span className="step" style={{ color: "#B00020" }}>
              DANGER ZONE
            </span>
            <div
              className="notice"
              style={{ background: "#FDF3F3", borderColor: "#F0C9C9" }}
            >
              <div>
                <p style={{ marginBottom: 10 }}>
                  <b style={{ color: "var(--navy)" }}>Delete this godown</b>
                  {agentCount > 0
                    ? " — reassign or remove its QC agents first; a godown with agents can't be deleted."
                    : " — this removes the godown. Its reports stay in the record but detach from it. This can't be undone."}
                </p>
                {confirmDelete ? (
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <button
                      type="button"
                      className="btn btn-danger"
                      disabled={delMut.isPending}
                      onClick={() => delMut.mutate()}
                    >
                      {delMut.isPending ? "Deleting…" : "Yes, delete godown"}
                    </button>
                    <button
                      type="button"
                      className="btn"
                      onClick={() => setConfirmDelete(false)}
                    >
                      Keep
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="btn"
                    onClick={() => setConfirmDelete(true)}
                  >
                    Delete godown
                  </button>
                )}
                {delMut.isError && (
                  <p style={{ color: "#B00020", marginTop: 8, fontSize: 12.5 }}>
                    {(delMut.error as Error).message}
                  </p>
                )}
              </div>
            </div>
          </div>
          <div className="drawer-foot">
            <button type="button" className="btn" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-gold"
              disabled={saveMut.isPending}
            >
              {saveMut.isPending ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      </aside>
    </>
  );
}
